import EventEmitter from 'events';
import WebSocket from 'isomorphic-ws';
import aceUIError from './error';
import Debug from '../../../../../ace-debug';

const COMPONENT_NAME = 'AceContentEvents';
const debug = new Debug (COMPONENT_NAME);

export default class extends EventEmitter {
/*
  #isPaused = false;
  #reconnectMode = true;
  #subscriptions = {};
*/
  constructor ({
    // DEBUG = false,
    keepAliveTimeout = 20000,
    reconnectMode = true,
    startInPausedMode = false,
    subscriptions = {},
    url,
    token
  }) {
    super ();

    if (!url) {
      throw aceUIError.Error({
        cause: 'No url for ace-content-events provided',
        origin: 'ace-content-events constructor',
        severity: aceUIError.severities.MAJOR,
        type: aceUIError.types.CONFIG_ERROR
      });
    }

    let keepAliveTimerID = 0;
    this.token = token;
    this._isPaused = { __ALL__: startInPausedMode };
    this._reconnectTimerID = 0;
    this._reconnectMode = reconnectMode;
    this._subscriptions = subscriptions;

    const connect = () => {
      debug.log (`Connecting to ${url} using ${token}`);
      this._ws = new WebSocket (`${url}?token=${this.token}`);
      this._ws.addEventListener ('error', wsError);
      this._ws.addEventListener ('open', wsOpen);
    };

    this._reconnect = () => {
      debug.log ('Reconnect.');
      if (!this._ws || this._ws.readyState === WebSocket.CLOSED) {
        connect ();
      } else if (this._ws.readyState === WebSocket.OPEN) {
        clearInterval (this._reconnectTimerID);
        this._reconnectTimerID = 0;
      }
    };

    const wsError = (error) => {
      debug.log ('WebSocket error', error);
      this.emit ('error', error);
    };

    const wsClose = (reason) => {
      debug.log ('WebSocket close', reason);
      clearInterval (keepAliveTimerID);

      if (this.reconnectMode && !this._reconnectTimerID) {
        debug.log ('Start reconnecting...');
        this._reconnectTimerID = setInterval (this._reconnect, 3000);
        debug.log ('Emitting connection-lost');
        this.emit ('connection-lost', reason);
      } else {
        debug.log ('Emitting close');
        this.emit ('close', reason);
      }

      // I'm not sure if listeners are removed when WebSocket is closed...
      debug.log ('Removing WebSocket listeners');
      this._ws.removeEventListener ('open', wsOpen);
      this._ws.removeEventListener ('error', wsError);
      this._ws.removeEventListener ('close', wsClose);

      debug.log ('Erasing WebSocket');
      this._ws = undefined;
    };

    const wsMessage = (message) => {
      if (message.data === JSON.stringify ({})) {
        debug.log ('pong');
        return;
      }

      debug.log ('WebSocket message', message);

      try {
        let event = JSON.parse (message.data);
        notifySubscribers (event, this._subscriptions);
      } catch (error) {
        this.emit ('error', error);
      }
    };

    const wsOpen = () => {
      debug.log ('WebSocket open');
      if (keepAliveTimeout) {
        keepAliveTimerID = setInterval (ping, keepAliveTimeout);
      }

      if (this._reconnectTimerID) {
        debug.log ('Exit reconnectMode in WebSocket open');
        clearInterval (this._reconnectTimerID);
        this._reconnectTimerID = 0;
        debug.log ('Emitting connection-resumed');
        this.emit ('connection-resumed');
      } else {
        debug.log ('Emitting open');
        this.emit ('open');
      }

      this._ws.addEventListener ('close', wsClose);
      this._ws.addEventListener ('message', wsMessage);
    };

    const notifySubscribers = (event) => {
      Object.keys (this._subscriptions).forEach ((subscription) => {
        let subscr = this._subscriptions[subscription];
        if (event.eventType === subscr.eventType) {
          if (subscr.system && Array.isArray (subscr.system.contentType)) {
            if (subscr.system.contentType.includes (event.system.contentType)) {
              if (this._isPaused.__ALL__ || this._isPaused[subscription]) {
                this.emit ('paused-event', { event, subscription });
              } else {
                this.emit (subscription, event);
              }
            }
          } else {
            if (this._isPaused.__ALL__ || this._isPaused[subscription]) {
              this.emit ('paused-event', { event, subscription });
            } else {
              this.emit (subscription, event);
            }
          }
        }
      });
    };

    const ping = () => {
      if (this._ws.readyState === WebSocket.OPEN) {
        debug.log ('ping');
        this._ws.send ('{"type":"ping"}');
      }
    };

    connect();
  }

  get reconnectMode () {
    return this._reconnectMode;
  }

  set reconnectMode (rm) {
    this._reconnectMode = rm;
    if (this._reconnectMode && !this._reconnectTimerID) {
      debug.log ('Enter reconnectMode');
      this._reconnectTimerID = setInterval (this._reconnect, 3000);
    } else if (!this._reconnectMode || this._reconnectTimerID) {
      debug.log ('Exit reconnectMode');
      clearInterval (this._reconnectTimerID);
      this._reconnectTimerID = 0;
    }
  }

  addSubscriptions (subscriptions) {
    let newSubscriptions = JSON.parse (JSON.stringify (subscriptions));
    Object.assign (this._subscriptions, newSubscriptions);
    Object.keys (newSubscriptions).forEach((subscriptionName) => {
      this.resume (subscriptionName);
    });
  }

  subscribe (subscriptionName, callback) {
    this.addListener (subscriptionName, callback);
    return () => {
      this.removeListener (subscriptionName, callback);
    };
  }

  pause (subscriptionName) {
    if (subscriptionName) {
      this._isPaused[subscriptionName] = true;
    } else {
      this._isPaused.__ALL__ = true;
    }
  }

  resume (subscriptionName) {
    if (subscriptionName) {
      this._isPaused[subscriptionName] = false;
    } else {
      this._isPaused.__ALL__ = false;
    }
  }

  stop () {
    this._reconnectMode = false;
    this._ws.close ();
  }
}
